import React, { useState } from 'react';
import './AdminImageDetail.css';
import { updateImage, getCategories, deleteImage } from '../../firebase/dataService';
import DeleteConfirmModal from './DeleteConfirmModal';

function AdminImageDetail({ image, images, currentIndex, onBack, onImageUpdated, onNavigate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    caption: image.caption || '',
    category: image.category || '',
    extractedText: image.extractedText || ''
  });
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // تحميل التصنيفات من Firebase
  React.useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // تحديث النموذج عند تغيير الصورة
  React.useEffect(() => {
    setEditForm({
      caption: image.caption || '',
      category: image.category || '',
      extractedText: image.extractedText || ''
    });
    setIsEditing(false);
  }, [image.id]);

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.className = `admin-notification ${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${type === 'success' ? '✅' : '❌'}</span>
        <span class="notification-message">${message}</span>
      </div>
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('show');
    }, 10);
    
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateImage(image.id, editForm);
      await onImageUpdated();
      setIsEditing(false);
      showNotification('تم حفظ التعديلات بنجاح', 'success');
    } catch (error) {
      console.error('Error updating image:', error);
      showNotification('حدث خطأ في حفظ التعديلات', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      caption: image.caption || '',
      category: image.category || '',
      extractedText: image.extractedText || ''
    });
    setIsEditing(false);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onNavigate(images[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      onNavigate(images[currentIndex + 1]);
    }
  };

  const downloadImage = async () => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${image.caption || 'تصميم'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      showNotification('تم تنزيل التصميم بنجاح', 'success');
    } catch (error) {
      console.error('Error downloading image:', error);
      // بديل احتياطي: فتح الصورة في تبويب جديد للحفظ اليدوي
      try {
        const link = document.createElement('a');
        link.href = image.url;
        link.download = `${image.caption || 'تصميم'}.jpg`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showNotification('تم فتح التصميم في تبويب جديد للحفظ', 'success');
      } catch (fallbackError) {
        showNotification('حدث خطأ في تنزيل التصميم', 'error');
      }
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteImage(image.id);
      showNotification('تم حذف التصميم بنجاح', 'success');
      setShowDeleteModal(false);
      
      // الانتقال للتصميم التالي أو السابق أو العودة للقائمة
      if (images.length > 1) {
        if (currentIndex < images.length - 1) {
          onNavigate(images[currentIndex + 1]);
        } else if (currentIndex > 0) {
          onNavigate(images[currentIndex - 1]);
        } else {
          onBack();
        }
      } else {
        onBack();
      }
      
      await onImageUpdated();
    } catch (error) {
      console.error('Error deleting image:', error);
      showNotification('حدث خطأ في حذف التصميم', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  console.log('AdminImageDetail rendered - Delete button should be visible');
  console.log('Deleting state:', deleting);
  console.log('IsEditing state:', isEditing);

  return (
    <div className="admin-image-detail">
      <div className="admin-detail-header">
        <button className="back-btn" onClick={onBack}>
          ← العودة للقائمة
        </button>
        
        <div className="navigation-btns">
          <button
            className="nav-btn"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            → السابق
          </button>
          <span className="image-counter">
            {currentIndex + 1} / {images.length}
          </span>
          <button
            className="nav-btn"
            onClick={handleNext}
            disabled={currentIndex === images.length - 1}
          >
            ← التالي
          </button>
          
          <button
            className="download-btn-header"
            onClick={downloadImage}
            disabled={deleting}
            title="تنزيل التصميم"
          >
            ⬇️ تنزيل
          </button>

          <button
            className={`edit-btn-header ${isEditing ? 'editing' : ''}`}
            onClick={() => setIsEditing(!isEditing)}
            disabled={deleting}
            title="تعديل التصميم"
          >
            {isEditing ? '❌ إلغاء' : '✏️ تعديل'}
          </button>
          
          <button
            className="delete-btn-header"
            onClick={handleDeleteClick}
            disabled={deleting}
            title="حذف التصميم"
          >
            🗑️ حذف
          </button>
        </div>
      </div>

      <div className="admin-detail-content">
        <div className="admin-detail-image-section">
          <img
            src={image.url}
            alt={image.caption}
            className="admin-detail-image"
          />
        </div>

        <div className="admin-detail-info-section">

          {isEditing ? (
            <div className="admin-edit-form">
              <div className="form-group">
                <label>العنوان:</label>
                <input
                  type="text"
                  value={editForm.caption}
                  onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                  placeholder="عنوان التصميم"
                />
              </div>

              <div className="form-group">
                <label>التصنيف:</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="category-select"
                >
                  <option value="">اختر التصنيف</option>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>التفريغ النصي:</label>
                <textarea
                  value={editForm.extractedText}
                  onChange={(e) => setEditForm({ ...editForm, extractedText: e.target.value })}
                  placeholder="التفريغ النصي للتصميم"
                  rows="15"
                />
              </div>

              <div className="form-actions">
                <button
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? '⏳ جاري الحفظ...' : '✅ حفظ التعديلات'}
                </button>
                <button
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  ❌ إلغاء
                </button>
              </div>
            </div>
          ) : (
            <div className="admin-view-mode">
              <div className="info-item">
                <h3 className="info-label">العنوان:</h3>
                <p className="info-value">{image.caption || 'بدون عنوان'}</p>
              </div>

              <div className="info-item">
                <h3 className="info-label">التصنيف:</h3>
                <span className="category-tag">{image.category || 'غير مصنف'}</span>
              </div>

              <div className="info-item">
                <h3 className="info-label">التفريغ النصي:</h3>
                <div className="extracted-text-box">
                  {image.extractedText ? (
                    <p className="extracted-text">{image.extractedText}</p>
                  ) : (
                    <p className="no-text">لا يوجد تفريغ نصي</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmModal
          title="حذف التصميم"
          message={`هل أنت متأكد من حذف التصميم "${image.caption}"؟`}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={deleting}
        />
      )}
    </div>
  );
}

export default AdminImageDetail;
